/**
 * IS THE DEPOSIT ON THIS QUOTE LAWFUL — ASKED AT THE QUOTE, WHICH IS THE ONLY PLACE IT CAN BE.
 *
 * The Product Owner's decision, 2026-09-28:
 *
 *   你不要现在就去算，你应该要发现跟他讲，这样可能是有 legal 的风险 … 应该是在报价那里才会发现。
 *
 * It used to be asked on the deposit policy screen, and the reason that was wrong is worth more
 * than the move: **a policy is not a charge.** A band saying "one month's rent at twelve months" is
 * unlawful only for a particular home, on a particular stay, once the fees actually being charged
 * are added to it. Deciding that in advance meant deciding it on facts that were not in yet — which
 * is why seven of forty-two homes came out as "not fully checked" rather than as an answer, and a
 * screen that half-answers a legal question teaches its reader to ignore it.
 *
 * At the quote every one of those facts exists: the home, the stay, the fees, the figure.
 *
 * IT WARNS. IT DOES NOT REFUSE, AND IT NEVER SILENTLY LOWERS THE NUMBER. Who decides what goes to a
 * renter is not this product's call — and a deposit this rule flags may still be the right thing to
 * send, because the rule applies to a residential tenancy and not every stay is one. So it goes in
 * `warnings`, which does not touch `ok`, beside `refusals`, which does.
 *
 * WHAT COUNTS TOWARD THE LIMIT, AND WHAT THE PRODUCT OWNER DECIDED DOES NOT. SMC 7.24.020 caps the
 * deposit PLUS non-refundable move-in fees. The cleaning fee is the one this business charges, and
 * he ruled it out on 2026-09-28: 「清洁费跟押金不能合在一起算」— it is the short-term rental's
 * move-out clean, on a stay that happens to run past thirty days, and SMC 7.24.030 permits a
 * non-refundable fee for exactly that. So cleaning is excluded, and every other one-time fee on the
 * quote is counted.
 *
 * Counted fees are NAMED in the warning. Nothing here knows whether a fee somebody added is
 * refundable, so a fee that should not have counted produces a warning whose own text shows why it
 * fired — legible and arguable, rather than a number nobody can reconstruct.
 *
 * THE FIRST MONTH'S RENT IS THE ONE THE RENTER PAYS, not the home's list rent. A January at −10%
 * makes the first month smaller, and a deposit of "100% of the monthly rent" is then larger than
 * the month it is measured against. That gap is invisible to any check made before the dates are
 * known, and it is the ordinary case rather than an exotic one.
 */

import { capFor } from '../property/depositCap.js'
import { amount } from './money.js'

const isCleaning = (f) => f.recurring === true || /clean/i.test(f.label ?? '')

/**
 * @returns `{ rule, code, message }` or `null` when there is nothing to say.
 *
 * `null` covers two different situations on purpose — the city has no rule, and the figures are
 * within it — because neither is something to tell somebody about on a screen they are trying to
 * send a quote from. The one absence that IS reported is a home with no city recorded, because
 * then nothing was checked and "nothing was checked" must not look like "it passed".
 */
export function depositLawWarning({ city, firstMonthRentCents, depositCents, fees = [] }) {
  if (!Number.isFinite(depositCents)) return null

  const counted = fees.filter((f) => !isCleaning(f) && Number.isFinite(f.amountCents) && f.amountCents > 0)
  const feeCents = counted.reduce((n, f) => n + f.amountCents, 0)

  const cap = capFor({ city, monthlyRent: Number.isFinite(firstMonthRentCents) ? firstMonthRentCents / 100 : null })

  if (cap.kind === 'NONE') return null
  if (cap.kind === 'UNKNOWN') {
    /*
      TWO GAPS REACH THIS BRANCH, AND THEY SEND SOMEBODY TO DIFFERENT FIELDS. (Copilot, mtr#112.)

      `capFor` answers UNKNOWN for a home with no city AND for one whose rent it cannot measure a
      ceiling against, and `missing` says which. The first version of this reported both as "no
      city is recorded", which sends whoever reads it to fix the wrong thing — the same defect
      `capFor` itself was corrected for on mtr#95, reintroduced one layer up.

      A missing rent cannot reach here through the engine, which refuses a quote without one long
      before this (P-01 / NO_RENT). It can reach here through the function, which is exported and
      tested on its own, and a function that diagnoses correctly only for its current caller is one
      that will be wrong for its next.
    */
    const noCity = cap.missing.includes('city')
    return {
      /*
        NOT SEATTLE'S CITATION. (Copilot, mtr#112.) Naming SMC 7.24.020 beside a message saying no
        local limit could be looked up tells an agent a Seattle ordinance applies to a home whose
        city nobody knows. The branch exists precisely because nothing is known.
      */
      rule: 'Not checked',
      code: 'DEPOSIT_NOT_CHECKED',
      message: noCity
        ? 'No city is recorded for this home, so the deposit was not checked against any local limit. '
          + 'That is not the same as passing one. Set the city on the home to have this answered.'
        : 'This home’s city has a deposit limit, and there is no rent here to measure it against, so '
          + 'the deposit was not checked. That is not the same as passing.',
    }
  }

  /*
    THE CEILING ROUNDS DOWN, WHICH IS THE ONE PLACE ROUNDING HAS A DIRECTION. (Copilot, mtr#112.)
    Rounding a deposit to the nearest cent is fine when the figure is ours; rounding UP into a
    legal limit is not, because a cent over the line is over the line. $47,000 a year is
    $3,916.666… a month, and to the nearest cent that is $3,916.67 — a figure one cent above the
    ordinance, presented as lawful.
  */
  const limitCents = Math.floor(cap.limit * 100)
  const chargedCents = depositCents + feeCents
  if (chargedCents <= limitCents) return null

  /* Written as every other amount is (money.js `amount`): "$1 over", not "$1.00 over"; cents only when there are some. */
  const d = amount
  const feeNames = counted.map((f) => `${f.label} ${d(f.amountCents)}`).join(', ')

  return {
    rule: cap.rule.label,
    code: 'DEPOSIT_OVER_CITY_LIMIT',
    message: `${cap.rule.says} This quote asks ${d(chargedCents)} — deposit ${d(depositCents)}`
      /*
        THE CEILING, NOT THE RENT IT CAME FROM. With an annual rate the two differ by the cent the
        ceiling rounds down, and `against a first month of $3,916.67 … $0.01 over` reads as
        arithmetic nobody can follow. The sentence above already says what the ceiling IS.
      */
      + `${feeCents > 0 ? ` plus ${feeNames}` : ''} — against a ceiling of ${d(limitCents)}, `
      + `which is ${d(chargedCents - limitCents)} over. This may be a legal risk. `
      + 'Nothing has been changed and the quote can still be sent; lower the deposit if it should not be.',
  }
}
